import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import { SignOut, User as UserIcon, List as ListIcon, X as CloseIcon, Car } from "@phosphor-icons/react";

export default function Navbar({ active }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const COMMUNITY_LINK = { to: "/community", label: "Community", icon: Car };

  const links = [];
  if (user?.role === "rider") {
    links.push({ to: "/rider", label: "Ride" });
    links.push({ to: "/rider/history", label: "History" });
    links.push({ to: "/leaderboard", label: "Leaderboard" });
    links.push(COMMUNITY_LINK);
    links.push({ to: "/account", label: "Account" });
  } else if (user?.role === "driver") {
    links.push({ to: "/driver", label: "Drive" });
    links.push({ to: "/driver/profile", label: "Profile" });
    links.push({ to: "/driver/earnings", label: "Earnings" });
    links.push({ to: "/driver/history", label: "Trips" });
    links.push({ to: "/leaderboard", label: "Leaderboard" });
    links.push(COMMUNITY_LINK);
    links.push({ to: "/account", label: "Account" });
  } else if (user?.role === "admin") {
    links.push({ to: "/admin", label: "Overview" });
    links.push({ to: "/admin/users", label: "Users" });
    links.push({ to: "/admin/drivers", label: "Drivers" });
    links.push({ to: "/admin/rides", label: "Rides" });
    links.push(COMMUNITY_LINK);
    links.push({ to: "/account", label: "Account" });
  }

  return (
    <header className="border-b-2 border-foreground bg-background sticky top-0 z-30">
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-4 sm:px-6 h-14">
        <button
          data-testid="nav-logo"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 font-display text-xl"
        >
          <span className="inline-block w-3 h-3 bg-primary" />
          RideGY
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`px-3 py-1.5 text-sm font-medium border-2 inline-flex items-center gap-1.5 ${
                  active === l.label.toLowerCase()
                    ? "border-foreground bg-foreground text-background"
                    : "border-transparent hover:border-foreground"
                }`}
              >
                {Icon && <Icon size={14} weight="fill" />}
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {!user && (
            <Link
              to="/community"
              data-testid="nav-community-public"
              className="inline-flex items-center gap-1.5 px-3 py-2 border-2 border-foreground bg-background hover:bg-foreground hover:text-background font-mono text-[11px] uppercase tracking-wider transition-colors"
              title="Ride Community"
            >
              <Car size={14} weight="fill" />
              <span className="hidden sm:inline">Ride Community</span>
              <span className="sm:hidden">Community</span>
            </Link>
          )}
          {user ? (
            <>
              {/* User chip — visible from sm+ on desktop, hidden on mobile (shown inside drawer) */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 border-2 border-border">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 object-cover" />
                ) : (
                  <UserIcon size={18} />
                )}
                <span className="text-sm font-medium" data-testid="nav-user-name">
                  {user.name}
                </span>
                {user.verified && (
                  <span
                    data-testid="nav-verified-badge"
                    title="Verified identity"
                    className="inline-flex items-center justify-center w-5 h-5 bg-accent text-white font-mono text-[10px]"
                  >
                    ✓
                  </span>
                )}
                <span className="text-xs font-mono uppercase tracking-wider px-1.5 py-0.5 bg-foreground text-background">
                  {user.role || "guest"}
                </span>
              </div>
              {/* Desktop sign out */}
              <Button
                data-testid="nav-logout-btn"
                variant="outline"
                size="sm"
                onClick={logout}
                className="hidden md:inline-flex rounded-none border-2 border-foreground"
              >
                <SignOut size={16} /> Sign out
              </Button>

              {/* Mobile hamburger */}
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button
                    data-testid="nav-mobile-toggle"
                    aria-label="Open menu"
                    className="md:hidden h-10 w-10 inline-flex items-center justify-center border-2 border-foreground bg-background hover:bg-foreground hover:text-background"
                  >
                    <ListIcon size={20} weight="bold" />
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  data-testid="nav-mobile-drawer"
                  className="w-[88%] max-w-sm border-l-2 border-foreground p-0 rounded-none [&>button]:hidden"
                >
                  <SheetHeader className="p-4 border-b-2 border-foreground flex flex-row items-center justify-between space-y-0">
                    <SheetTitle className="font-display text-xl flex items-center gap-2">
                      <span className="inline-block w-3 h-3 bg-primary" /> RideGY
                    </SheetTitle>
                    <button
                      data-testid="nav-mobile-close"
                      onClick={() => setOpen(false)}
                      aria-label="Close menu"
                      className="h-9 w-9 inline-flex items-center justify-center border-2 border-foreground"
                    >
                      <CloseIcon size={16} weight="bold" />
                    </button>
                  </SheetHeader>

                  <div className="p-4 border-b-2 border-foreground flex items-center gap-3">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-12 h-12 object-cover border-2 border-foreground" />
                    ) : (
                      <div className="w-12 h-12 bg-foreground text-background inline-flex items-center justify-center font-display text-lg">
                        {(user.name || "U").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-base leading-tight truncate flex items-center gap-1.5">
                        {user.name}
                        {user.verified && (
                          <span className="inline-flex items-center justify-center w-4 h-4 bg-accent text-white font-mono text-[9px]">✓</span>
                        )}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60 truncate">
                        {user.email}
                      </div>
                      <span className="mt-1 inline-block text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-foreground text-background">
                        {user.role || "guest"}
                      </span>
                    </div>
                  </div>

                  <nav className="flex flex-col p-2" data-testid="nav-mobile-links">
                    {links.map((l) => {
                      const Icon = l.icon;
                      return (
                        <Link
                          key={l.to}
                          to={l.to}
                          data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                          onClick={() => setOpen(false)}
                          className={`px-4 py-3 text-base font-medium border-2 mb-1 inline-flex items-center gap-2 ${
                            active === l.label.toLowerCase()
                              ? "border-foreground bg-foreground text-background"
                              : "border-transparent hover:border-foreground"
                          }`}
                        >
                          {Icon && <Icon size={18} weight="fill" />}
                          {l.label}
                        </Link>
                      );
                    })}
                  </nav>

                  <div className="p-4 border-t-2 border-foreground mt-auto">
                    <Button
                      data-testid="nav-mobile-logout-btn"
                      variant="outline"
                      onClick={() => { setOpen(false); logout(); }}
                      className="w-full rounded-none border-2 border-foreground"
                    >
                      <SignOut size={16} /> Sign out
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}
